import { useState, useEffect } from 'react';
import { AlertCircle, CreditCard, ExternalLink, CheckCircle2, Clock, X, RefreshCw, Shield, Lock, Building } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import StripeConnectOnboardingModal from './StripeConnectOnboardingModal';

const StripeConnectBanner = ({ onDismiss }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showTrustModal, setShowTrustModal] = useState(false);
  const [showVerifyInfoModal, setShowVerifyInfoModal] = useState(false);

  useEffect(() => {
    checkStatus();
  }, []);

  const checkStatus = async () => {
    try {
      const response = await api.get('/organizations/stripe-connect/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Error checking Stripe Connect status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = () => {
    // Show trust modal first before Stripe onboarding
    setShowTrustModal(true);
  };
  
  const handleConfirmStripe = () => {
    setShowTrustModal(false);
    setShowOnboardingModal(true);
  };

  const handleOnboardingComplete = () => {
    setShowOnboardingModal(false);
    toast.success('Stripe setup submitted! Checking status...');
    
    // Notify setup guide of data change
    window.dispatchEvent(new Event('setupDataChanged'));
    
    // Refresh status after a short delay to allow Stripe to process
    setTimeout(() => {
      checkStatus();
      toast.success('Status updated!');
    }, 2000);
  };

  const handleRefresh = () => {
    // Show info modal first before continuing verification
    setShowVerifyInfoModal(true);
  };
  
  const handleContinueVerification = () => {
    setShowVerifyInfoModal(false);
    setShowOnboardingModal(true);
  };

  if (loading) {
    return null;
  }

  // Don't show if already fully connected
  if (status?.onboardingComplete) {
    return null;
  }

  // Show different banners based on status
  if (!status?.connected) {
    // Not connected at all
    return (
      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6 relative">
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        )}
        
        <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="bg-blue-600 p-2 sm:p-3 rounded-xl flex-shrink-0">
            <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              💰 Set Up Payment Processing
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-3">
              Connect your payment account to start receiving payments from customers. 
              Setup takes just 2-3 minutes. Payments go directly to your bank account.
            </p>
            <p className="text-xs sm:text-sm text-gray-600 mb-4">
              ⏱️ <em>Note: Verification may take a few minutes after submitting your form. Hang tight!</em>
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <button
                onClick={handleConnect}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-6 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <CreditCard className="w-4 h-4" />
                Connect Payment Account
              </button>
              <button
                onClick={checkStatus}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4" />
                Check Status
              </button>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 justify-center sm:justify-start">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Free • Secure • Takes 2-3 min
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust Modal - Shows before Stripe onboarding */}
        {showTrustModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Secure Payment Setup</h3>
                    <p className="text-blue-100 text-sm">Powered by Stripe</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <p className="text-gray-700">
                  <strong>BookingFlows partners with Stripe</strong> — the world's most trusted payment platform used by millions of businesses.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 p-1.5 rounded-full mt-0.5">
                      <Lock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your Money Goes Directly to You</p>
                      <p className="text-sm text-gray-600">Payments deposit directly into your bank account. We never hold your funds.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                      <Shield className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">We Don't Store Your Financial Data</p>
                      <p className="text-sm text-gray-600">All sensitive payment and banking information is handled securely by Stripe — not us.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-purple-100 p-1.5 rounded-full mt-0.5">
                      <Building className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">PCI Compliant & Bank-Level Security</p>
                      <p className="text-sm text-gray-600">Stripe maintains the highest security standards so you don't have to worry.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  <p>You'll be guided through Stripe's secure setup process. This typically takes 2-3 minutes.</p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowTrustModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmStripe}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  Continue to Stripe
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Onboarding Modal */}
        <StripeConnectOnboardingModal
          isOpen={showOnboardingModal}
          onClose={() => setShowOnboardingModal(false)}
          onComplete={handleOnboardingComplete}
        />
      </div>
    );
  }

  // Connected but onboarding incomplete
  if (status?.connected && !status?.onboardingComplete) {
    return (
      <div className="mb-6">
        {/* Progress Indicator - Mobile Responsive */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 mb-6">
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
            ✓
          </div>
          <div className="w-8 sm:w-12 h-1 bg-green-600" />
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs sm:text-sm font-semibold">
            2
          </div>
          <div className="w-8 sm:w-12 h-1 bg-gray-200" />
          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs sm:text-sm font-semibold">
            3
          </div>
        </div>
      
        {/* Card */}
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 sm:p-6 relative">
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          )}
          
          <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4">
          <div className="bg-green-600 p-2 sm:p-3 rounded-xl flex-shrink-0">
            <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
              Almost There! 🎉
            </h3>
            <p className="text-sm sm:text-base text-gray-700 mb-2">
              Great progress! Your documents for your business were verified successfully. ✓
            </p>
            <p className="text-sm sm:text-base text-gray-700 mb-4">
              We just need to verify a few more details about you (the owner) to finish setting up payments. Just a few clicks away!
            </p>
            {status?.requirements?.currentlyDue?.length > 0 && (
              <div className="bg-green-50 rounded-lg p-3 sm:p-4 mb-4 border border-green-200">
                <p className="text-xs sm:text-sm font-semibold text-green-900 mb-2">Quick verifications needed:</p>
                <ul className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5">
                  {status.requirements.currentlyDue.slice(0, 5).map((req, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 sm:gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full flex-shrink-0 mt-1.5" />
                      <span className="capitalize break-all flex-1">{req.replace(/_/g, ' ').replace('individual', 'your')}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={handleRefresh}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <CheckCircle2 className="w-4 h-4" />
                Verify Owner Info
              </button>
              <button
                onClick={() => window.location.reload()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors text-sm sm:text-base"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh Status</span>
                <span className="sm:hidden">Refresh</span>
              </button>
            </div>
          </div>
        </div>
        </div>
        
        
        {/* Verify Info Modal - Explains what to expect in verification */}
        {showVerifyInfoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Clock className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">What to Expect</h3>
                    <p className="text-green-100 text-sm">Finishing Your Stripe Setup</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <p className="text-gray-700">
                  <strong>You're almost there!</strong> Stripe's verification process varies for each user based on your business type and location.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-amber-100 p-1.5 rounded-full mt-0.5">
                      <Clock className="w-4 h-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Processing Takes Time</p>
                      <p className="text-sm text-gray-600">Verification may take a few minutes to a few hours. Be patient!</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                      <CreditCard className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">You May Need to Verify Your Identity</p>
                      <p className="text-sm text-gray-600">Stripe may ask for a photo ID, government identification number, or business documents depending on your region.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 p-1.5 rounded-full mt-0.5">
                      <RefreshCw className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Refresh to Check Progress</p>
                      <p className="text-sm text-gray-600">After submitting, use "Refresh Status" to see if verification is complete.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-blue-50 rounded-lg p-3 text-sm text-gray-700 border border-blue-200">
                  <p><strong>💡 Tip:</strong> Be patient and persistent. If you're stuck, reach out to our support team — we're happy to help!</p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowVerifyInfoModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContinueVerification}
                  className="flex-1 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  Continue
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Trust Modal - Shows before Stripe onboarding */}
        {showTrustModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 text-white">
                <div className="flex items-center gap-3">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold">Secure Payment Setup</h3>
                    <p className="text-blue-100 text-sm">Powered by Stripe</p>
                  </div>
                </div>
              </div>
              
              {/* Content */}
              <div className="px-6 py-5 space-y-4">
                <p className="text-gray-700">
                  <strong>BookingFlows partners with Stripe</strong> — the world's most trusted payment platform used by millions of businesses.
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="bg-green-100 p-1.5 rounded-full mt-0.5">
                      <Lock className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your Money Goes Directly to You</p>
                      <p className="text-sm text-gray-600">Payments deposit directly into your bank account. We never hold your funds.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 p-1.5 rounded-full mt-0.5">
                      <Shield className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">We Don't Store Your Financial Data</p>
                      <p className="text-sm text-gray-600">All sensitive payment and banking information is handled securely by Stripe — not us.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="bg-purple-100 p-1.5 rounded-full mt-0.5">
                      <Building className="w-4 h-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">PCI Compliant & Bank-Level Security</p>
                      <p className="text-sm text-gray-600">Stripe maintains the highest security standards so you don't have to worry.</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600">
                  <p>You'll be guided through Stripe's secure setup process. This typically takes 2-3 minutes.</p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button
                  onClick={() => setShowTrustModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmStripe}
                  className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  Continue to Stripe
                  <ExternalLink className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Onboarding Modal */}
        <StripeConnectOnboardingModal
          isOpen={showOnboardingModal}
          onClose={() => setShowOnboardingModal(false)}
          onComplete={handleOnboardingComplete}
        />
      </div>
    );
  }

  return null;
};

export default StripeConnectBanner;
